import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  IconButton
} from '@mui/material';
import { CalendarToday, EventAvailable, EventBusy, ChevronLeft, ChevronRight, SouthEast, NorthWest } from '@mui/icons-material';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  formatLocalDateKey,
  getMonthCalendarDays,
  startOfLocalToday
} from '../utils/calendarDateKeys';

const AvailabilityCalendar = ({ yacht }) => {
  const { isAdmin } = useAuth();
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const yachtDoc = await getDoc(doc(db, 'yachts', yacht.id));
        if (yachtDoc.exists()) {
          const yachtData = yachtDoc.data();
          setAvailability(yachtData.availability || {});
        }
      } catch (error) {
        console.error('Error fetching availability:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailability();
  }, [yacht.id]);

  const getDateKey = (date) => formatLocalDateKey(date);

  const getAvailabilityStatus = (date) => {
    const dateKey = getDateKey(date);
    return availability[dateKey]?.status || 'available';
  };

  const getDatePrice = (date) => {
    const dateKey = getDateKey(date);
    return availability[dateKey]?.price || null;
  };

  const isOffSeasonDate = (date) => {
    const month = date.getMonth() + 1; // 1-12
    const day = date.getDate();
    if (month === 10 && day >= 12) return true; // od 12 października
    if (month >= 11 || month <= 3) return true; // listopad-marzec
    return false;
  };

  const toggleAvailability = async (date) => {
    if (!isAdmin) return;
    
    const dateKey = getDateKey(date);
    const currentData = availability[dateKey] || { status: 'available', price: null };
    const normalizeStatus = (status) => (status === 'changeover' ? 'changeover_start' : status);
    const statusOrder = ['available', 'changeover_start', 'booked', 'changeover_end'];
    const currentIndex = statusOrder.indexOf(normalizeStatus(currentData.status));
    const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % statusOrder.length;
    const newStatus = statusOrder[nextIndex];
    
    try {
      const newAvailability = { 
        ...availability, 
        [dateKey]: { 
          ...currentData, 
          status: newStatus 
        } 
      };
      await updateDoc(doc(db, 'yachts', yacht.id), {
        availability: newAvailability
      });
      setAvailability(newAvailability);
    } catch (error) {
      console.error('Error updating availability:', error);
    }
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const monthDays = getMonthCalendarDays(currentMonth);
  const todayStart = startOfLocalToday();
  const monthNames = [
    'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
    'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
  ];

  if (loading) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography>Ładowanie kalendarza...</Typography>
      </Paper>
    );
  }

  return (
    <Paper 
      elevation={1} 
      sx={{ 
        p: 3, 
        borderRadius: 2,
        border: '1px solid #e0e0e0',
        width: 'fit-content',
        minWidth: '420px'
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        mb: 3,
        pb: 2,
        borderBottom: '1px solid #e0e0e0',
        flexWrap: 'wrap',
        gap: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
          <CalendarToday sx={{ mr: 1, color: '#1565c0', flexShrink: 0, fontSize: 28 }} />
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 500, 
              color: '#333',
              fontSize: '1.1rem'
            }}
          >
            Kalendarz Dostępności
          </Typography>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          flexShrink: 0
        }}>
          <IconButton 
            size="small" 
            onClick={() => navigateMonth(-1)}
            sx={{ 
              color: '#1565c0',
              '&:hover': {
                backgroundColor: '#e3f2fd',
                color: '#0d47a1'
              }
            }}
          >
            <ChevronLeft />
          </IconButton>
          <Typography 
            variant="body1" 
            sx={{ 
              minWidth: 140, 
              textAlign: 'center',
              fontWeight: 500,
              color: '#333',
              fontSize: '1rem',
              whiteSpace: 'nowrap'
            }}
          >
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </Typography>
          <IconButton 
            size="small" 
            onClick={() => navigateMonth(1)}
            sx={{ 
              color: '#1565c0',
              '&:hover': {
                backgroundColor: '#e3f2fd',
                color: '#0d47a1'
              }
            }}
          >
            <ChevronRight />
          </IconButton>
        </Box>
      </Box>
      
      {isAdmin ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Kliknij na datę, aby zmienić status (dostępny → początek czarteru → zajęty → koniec czarteru)
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Kalendarz dostępności - tylko do odczytu. Skontaktuj się z nami, aby zarezerwować jacht.
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        Poza sezonem (12 października - 31 marca) terminy są niedostępne.
      </Typography>

      {/* Day headers */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(7, 1fr)', 
        gap: 1, 
        mb: 2 
      }}>
        {['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Niedz'].map((day, index) => (
          <Box key={day} sx={{
            p: 1.5,
            textAlign: 'center',
            bgcolor: index >= 5 ? '#e3f2fd' : '#f5f5f5',
            borderRadius: 1,
            border: index >= 5 ? '1px solid #2196f3' : '1px solid #e0e0e0',
            minWidth: '60px' // Ensure adequate width
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600, 
                color: index >= 5 ? '#1565c0' : '#424242',
                fontSize: '0.875rem'
              }}
            >
              {day}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Calendar grid */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(7, 1fr)', 
        gap: 1,
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        minWidth: '420px' // Ensure minimum width for proper display
      }}>
        {monthDays.map((date, index) => {
          if (!date) {
            return <Box key={`empty-${index}`} sx={{ height: 80, minWidth: '60px' }} />;
          }
          
          const status = getAvailabilityStatus(date);
          const price = getDatePrice(date);
          const isToday = date.toDateString() === new Date().toDateString();
          const isPast = date < todayStart;
          const isOffSeason = isOffSeasonDate(date);
          const isDisabled = isPast || isOffSeason;
          const normalizedStatus = status === 'changeover' ? 'changeover_start' : status;
          const isChangeoverStart = normalizedStatus === 'changeover_start';
          const isChangeoverEnd = normalizedStatus === 'changeover_end';
          const isChangeover = isChangeoverStart || isChangeoverEnd;

          return (
            <Box
              key={index}
              sx={{
                height: 80,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1,
                border: isToday ? '3px solid #1565c0' : '1px solid #e0e0e0',
                borderRadius: 1,
                cursor: isAdmin && !isDisabled ? 'pointer' : 'default',
                background: isDisabled
                  ? '#e0e0e0' 
                  : isChangeoverStart
                    ? 'linear-gradient(135deg, #c8e6c9 0 46%, #f5f5f5 46% 54%, #ffcc80 54% 100%)'
                    : isChangeoverEnd
                      ? 'linear-gradient(135deg, #ffcc80 0 46%, #f5f5f5 46% 54%, #c8e6c9 54% 100%)'
                    : normalizedStatus === 'available'
                      ? '#c8e6c9'
                      : '#ffcc80',
                opacity: isDisabled ? 0.7 : 1,
                transition: 'all 0.2s ease-in-out',
                minWidth: '60px', // Ensure adequate width
                '&:hover': isAdmin && !isDisabled ? {
                  background: isChangeoverStart
                    ? 'linear-gradient(135deg, #a5d6a7 0 46%, #f0f0f0 46% 54%, #ffb74d 54% 100%)'
                    : isChangeoverEnd
                      ? 'linear-gradient(135deg, #ffb74d 0 46%, #f0f0f0 46% 54%, #a5d6a7 54% 100%)'
                    : normalizedStatus === 'available'
                      ? '#a5d6a7'
                      : '#ffb74d',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                } : {}
              }}
              onClick={() => !isDisabled && toggleAvailability(date)}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: '0.875rem', 
                  fontWeight: isToday ? 600 : 500,
                  color: isDisabled ? '#999' : '#333',
                  lineHeight: 1,
                  mb: 0.5
                }}
              >
                {date.getDate()}
              </Typography>
              
              {price && !isDisabled && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.75rem', 
                    color: '#1565c0',
                    fontWeight: 500,
                    lineHeight: 1,
                    textAlign: 'center',
                    mb: 0.5
                  }}
                >
                  {price} zł
                </Typography>
              )}
              
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 18,
                height: 18,
                borderRadius: '50%',
                bgcolor: !isDisabled
                  ? (
                    normalizedStatus === 'available'
                      ? '#2e7d32'
                      : normalizedStatus === 'changeover_start'
                        ? '#1565c0'
                        : normalizedStatus === 'changeover_end'
                          ? '#6a1b9a'
                          : '#f57c00'
                  ) 
                  : '#9e9e9e',
                color: 'white',
                flexShrink: 0
              }}>
                {!isDisabled && (
                  normalizedStatus === 'available' ? (
                    <EventAvailable sx={{ fontSize: 12 }} />
                  ) : normalizedStatus === 'changeover_start' ? (
                    <SouthEast sx={{ fontSize: 11 }} />
                  ) : normalizedStatus === 'changeover_end' ? (
                    <NorthWest sx={{ fontSize: 11 }} />
                  ) : (
                    <EventBusy sx={{ fontSize: 12 }} />
                  )
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ 
        mt: 3, 
        display: 'flex', 
        gap: 2, 
        justifyContent: 'center',
        flexWrap: 'wrap'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          bgcolor: '#c8e6c9',
          borderRadius: 1,
          border: '2px solid #2e7d32'
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            borderRadius: '50%',
            bgcolor: '#2e7d32',
            color: 'white'
          }}>
            <EventAvailable sx={{ fontSize: 12 }} />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
            Dostępny
          </Typography>
        </Box>
        
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          bgcolor: '#ffcc80',
          borderRadius: 1,
          border: '2px solid #f57c00'
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            borderRadius: '50%',
            bgcolor: '#f57c00',
            color: 'white'
          }}>
            <EventBusy sx={{ fontSize: 12 }} />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
            Zajęty
          </Typography>
        </Box>

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          bgcolor: '#e3f2fd',
          borderRadius: 1,
          border: '2px solid #1565c0'
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            borderRadius: '50%',
            bgcolor: '#1565c0',
            color: 'white'
          }}>
            <CalendarToday sx={{ fontSize: 11 }} />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
            Początek czarteru
          </Typography>
        </Box>

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          bgcolor: '#f3e5f5',
          borderRadius: 1,
          border: '2px solid #6a1b9a'
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            borderRadius: '50%',
            bgcolor: '#6a1b9a',
            color: 'white'
          }}>
            <NorthWest sx={{ fontSize: 11 }} />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
            Koniec czarteru
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default AvailabilityCalendar;
